import React from "react";
import {
  AbsoluteFill,
  interpolate,
  OffthreadVideo,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// Helper Component: Paper Tape with Masking Tape Strips on Both Ends (Iconic Reel Style)
const PaperTape: React.FC<{
  text: string;
  subtext?: string;
  rotate?: number;
  scale?: number;
  bgColor?: string;
  textColor?: string;
  fontSize?: number;
}> = ({
  text,
  subtext,
  rotate = -1.5,
  scale = 1,
  bgColor = "#F4EFE6", // Natural Warm Japanese Paper
  textColor = "#18181B",
  fontSize = 32,
}) => {
  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: bgColor,
        padding: "16px 42px",
        borderRadius: 4,
        transform: `rotate(${rotate}deg) scale(${scale})`,
        boxShadow: "0 10px 25px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.2)",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      {/* Scotch Tape / Masking Tape Top-Left */}
      <div
        style={{
          position: "absolute",
          top: -14,
          left: -18,
          width: 56,
          height: 26,
          backgroundColor: "rgba(255, 250, 235, 0.65)",
          backdropFilter: "blur(2px)",
          border: "1px dashed rgba(200, 190, 170, 0.6)",
          transform: "rotate(-24deg)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      />
      {/* Scotch Tape / Masking Tape Top-Right */}
      <div
        style={{
          position: "absolute",
          top: -12,
          right: -18,
          width: 54,
          height: 26,
          backgroundColor: "rgba(255, 250, 235, 0.65)",
          backdropFilter: "blur(2px)",
          border: "1px dashed rgba(200, 190, 170, 0.6)",
          transform: "rotate(18deg)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      />

      {/* Main Text */}
      <span
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 900,
          fontSize,
          letterSpacing: -0.5,
          color: textColor,
          textTransform: "uppercase",
          lineHeight: 1.1,
          textAlign: "center",
        }}
      >
        {text}
      </span>
      {subtext && (
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: fontSize * 0.45,
            letterSpacing: 1.5,
            color: "rgba(24, 24, 27, 0.7)",
            marginTop: 6,
            textTransform: "uppercase",
          }}
        >
          {subtext}
        </span>
      )}
    </div>
  );
};

// Small Floating Sticky Note Tag
const StickyTag: React.FC<{
  label: string;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  rotate?: number;
  scale?: number;
  color?: string;
}> = ({ label, top, bottom, left, right, rotate = 2, scale = 1, color = "#FDE047" }) => {
  return (
    <div
      style={{
        position: "absolute",
        top,
        bottom,
        left,
        right,
        transform: `rotate(${rotate}deg) scale(${scale})`,
        backgroundColor: color,
        color: "#18181B",
        padding: "8px 18px",
        borderRadius: 3,
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
        zIndex: 25,
      }}
    >
      {/* Tape on top center */}
      <div
        style={{
          position: "absolute",
          top: -8,
          left: "50%",
          transform: "translateX(-50%) rotate(-3deg)",
          width: 32,
          height: 14,
          backgroundColor: "rgba(255, 255, 255, 0.65)",
          border: "0.5px solid rgba(0,0,0,0.1)",
        }}
      />
      {label}
    </div>
  );
};

export const SejarahSumurBorRefStyle: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 4 Narrative Beats across 240 frames (8 seconds @ 30fps)
  // Phase 1: Frame 0 - 65   (Macro deep drill bit in bedrock)
  // Phase 2: Frame 65 - 125 (Han Dynasty & Bamboo rig elevator zoom-out)
  // Phase 3: Frame 125 - 185(Artois Artesian natural hydraulic fountain)
  // Phase 4: Frame 185 - 240(Modern Rotary Bit grinding hard stone)
  const phase =
    frame < 65 ? 1 : frame < 125 ? 2 : frame < 185 ? 3 : 4;

  const phaseLocalFrame =
    frame < 65
      ? frame
      : frame < 125
      ? frame - 65
      : frame < 185
      ? frame - 125
      : frame - 185;

  // Spring animation for punchy paper transitions on each beat
  const popSpring = spring({
    frame: phaseLocalFrame,
    fps,
    config: { damping: 13, mass: 0.65 },
  });

  const headerSpring = spring({
    frame,
    fps,
    config: { damping: 14, mass: 0.8 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0A0C10",
        overflow: "hidden",
      }}
    >
      {/* 1. FULL-CANVAS 3D CINEMATIC ANIMATION FROM LAPTOP RTX 3050 */}
      <AbsoluteFill>
        <OffthreadVideo
          src={staticFile("sumur_bor/trackto_intra.mp4")}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scale(1.02)",
          }}
        />
      </AbsoluteFill>

      {/* 2. SUBTLE VIGNETTE MASKS FOR LEGIBILITY */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 380,
          background:
            "linear-gradient(180deg, rgba(10,12,16,0.85) 0%, rgba(10,12,16,0.35) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 520,
          background:
            "linear-gradient(0deg, rgba(10,12,16,0.92) 0%, rgba(10,12,16,0.45) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. TOP PAPER TAPE BANNER (Raised to Y: 180px for Clear Rig Headroom) */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            headerSpring,
            [0, 1],
            [-40, 0]
          )}px)`,
          zIndex: 30,
        }}
      >
        <PaperTape
          text="SEJARAH TERCIPTANYA SUMUR BOR"
          subtext="REKAYASA TEKNOLOGI // BUKAN AI GENERATIVE"
          rotate={-1.5}
          scale={headerSpring}
          fontSize={34}
        />
      </div>

      {/* 4. DYNAMIC FLOATING STICKY TAGS (POP ACCORDING TO 3D CAMERA POSITION) */}
      {/* In Phase 1: Pointing to the deep drill bit & bedrock */}
      {phase === 1 && (
        <>
          <StickyTag
            label="MATA BOR TRI-CONE"
            top={560}
            left={140}
            rotate={-4}
            scale={popSpring}
            color="#FEF08A"
          />
          <StickyTag
            label="BATUAN CADAS KERAS"
            top={720}
            right={120}
            rotate={3}
            scale={popSpring}
            color="#BAE6FD"
          />
        </>
      )}

      {/* In Phase 2: Elevator Zoom-Out through Strata */}
      {phase === 2 && (
        <>
          <StickyTag
            label="PENETRASI 1.000 METER"
            top={480}
            right={120}
            rotate={-2.5}
            scale={popSpring}
            color="#FED7AA"
          />
          <StickyTag
            label="LEMPUNG KEDAP AIR"
            top={680}
            left={120}
            rotate={4}
            scale={popSpring}
            color="#BBF7D0"
          />
        </>
      )}

      {/* In Phase 3: Surface Derrick Rig Revealed */}
      {phase === 3 && (
        <>
          <StickyTag
            label="DERRICK RIG PERMUKAAN"
            top={440}
            left={130}
            rotate={-3}
            scale={popSpring}
            color="#FEF08A"
          />
          <StickyTag
            label="SEMBURAN HIDROLIK ARTESIS"
            top={620}
            right={110}
            rotate={2.5}
            scale={popSpring}
            color="#7DD3FC"
          />
        </>
      )}

      {/* In Phase 4: Full Isometric View & Gushing Artesian Fountain */}
      {phase === 4 && (
        <>
          <StickyTag
            label="AKUIFER MURNI RIBUAN TAHUN"
            top={780}
            right={160}
            rotate={-2}
            scale={popSpring}
            color="#38BDF8"
          />
          <StickyTag
            label="BEBAS CEMARAN DANGKAL"
            top={500}
            left={140}
            rotate={3.5}
            scale={popSpring}
            color="#F472B6"
          />
        </>
      )}

      {/* 5. BOTTOM PAPER TAPE SUBTITLES (Safe Zone Y: 1220 - 1500px) */}
      <div
        style={{
          position: "absolute",
          bottom: 390,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            popSpring,
            [0, 1],
            [30, 0]
          )}px)`,
          width: 900,
          display: "flex",
          justifyContent: "center",
          zIndex: 30,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            backgroundColor: "#F7F4EB", // Textured Paper Note
            padding: "26px 36px",
            borderRadius: 6,
            transform: `rotate(${phase % 2 === 0 ? 1.2 : -1.2}deg) scale(${popSpring})`,
            boxShadow: "0 16px 35px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.3)",
            border: "1.5px solid rgba(0,0,0,0.1)",
          }}
        >
          {/* Top Masking Tape Stripe */}
          <div
            style={{
              position: "absolute",
              top: -16,
              left: "50%",
              transform: "translateX(-50%) rotate(1deg)",
              width: 140,
              height: 32,
              backgroundColor: "rgba(255, 250, 235, 0.75)",
              border: "1px dashed rgba(180, 170, 150, 0.5)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          />

          {/* Phase Badge */}
          <div
            style={{
              display: "inline-block",
              backgroundColor: "#18181B",
              color: "#FEF08A",
              padding: "4px 14px",
              borderRadius: 3,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            {phase === 1 && "FASE 1: MASALAH SUMUR GALI DANGKAL"}
            {phase === 2 && "FASE 2: DINASTI HAN (200 SM - BOR BAMBU)"}
            {phase === 3 && "FASE 3: LEMBAH ARTOIS (1126 M - AIR MEMANCAR)"}
            {phase === 4 && "FASE 4: ERA MODERN (ROTARY ROLLER BIT)"}
          </div>

          {/* Punchy Narrative Content */}
          <p
            style={{
              margin: 0,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 24,
              fontWeight: 800,
              lineHeight: 1.35,
              color: "#18181B",
              textTransform: "uppercase",
              letterSpacing: -0.3,
            }}
          >
            {phase === 1 && (
              <>
                Dulu sumur cuma digali tanah dangkal. Airnya gampang kering &{" "}
                <span style={{ color: "#DC2626" }}>rawan tercemar bakteri</span> dari kotoran
                permukaan!
              </>
            )}
            {phase === 2 && (
              <>
                Tahun 200 SM di China, lahir teknik bor tumbuk pakai pipa bambu. Tembus bumi{" "}
                <span style={{ color: "#0284C7" }}>hingga 1.000 meter</span> demi air garam & gas!
              </>
            )}
            {phase === 3 && (
              <>
                Tahun 1126 di Lembah Artois Prancis, air memancar sendiri ke atas tanpa ditimba—itulah{" "}
                <span style={{ color: "#0284C7" }}>Sumur Artesis</span> bertekanan hidrolik alami!
              </>
            )}
            {phase === 4 && (
              <>
                Di era modern, teknologi roller bit menggerus batu granit cadas keras, mengambil{" "}
                <span style={{ color: "#059669" }}>air tanah murni</span> yang tersaring ribuan
                tahun!
              </>
            )}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  );
};
